import type { PblOcLawyer, PblOcLawyerFilters, PblOcManagedCase, PblOcMatchingView } from './types';
import { buildReviewSummaryFromProfile } from '@/components/Pbl/VolunteerRegistration/reviewSummary';
import type { VolunteerFormState } from '@/components/Pbl/VolunteerRegistration/types';
import { pblLanguages, pblProfessionalQualifications, pblServiceAreas } from '@/data/pbl';
import type {
  PblCaseStatus,
  PblDirectoryEntry,
  PblLawyerProfile,
  PblPublicDirectoryEntry,
  PblPublicLawyerProfile,
  PblManualInvitePayload,
  PblServiceArea,
} from '@/types/pbl';

type PblLawyerCardProfile = PblLawyerProfile | PblPublicLawyerProfile;

const isFullPblLawyerProfile = (profile: PblLawyerCardProfile): profile is PblLawyerProfile =>
  'email' in profile && typeof profile.email === 'string' && 'phone' in profile && typeof profile.phone === 'string';

const normalize = (value: string) => value.trim().toLowerCase();
const getCurrentYear = () => new Date().getFullYear();
const getCurrentFormDate = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const practiceAreaToServiceArea = (practiceArea: string): PblServiceArea => {
  const normalizedPracticeArea = normalize(practiceArea);

  if (normalizedPracticeArea.includes('family')) return 'family_matters';
  if (normalizedPracticeArea.includes('employment')) return 'employment_disputes';
  if (normalizedPracticeArea.includes('building')) return 'building_management';
  if (normalizedPracticeArea.includes('tenancy')) return 'tenancy_and_properties';
  if (normalizedPracticeArea.includes('criminal')) return 'criminal_matters';

  return 'estate_planning';
};

const serviceAreaLabelByValue = pblServiceAreas.reduce<Record<string, string>>((labels, option) => {
  labels[option.value] = option.defaultLabel;
  return labels;
}, {});

const optionLabel = <Option extends { value: string; defaultLabel: string }>(
  options: readonly Option[],
  value?: string
) => {
  if (!value) return '';
  return options.find((option) => option.value === value)?.defaultLabel || value;
};

const joinLabels = <Option extends { value: string; defaultLabel: string }>(
  options: readonly Option[],
  values?: string[] | null
) =>
  (values || []).map((value) => optionLabel(options, value)).filter(Boolean).join(', ');

const splitDisplayList = (value?: string | null) =>
  value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const trimDisplayText = (value?: string | null) => value?.trim() || '';

const getProfileQualification = (profile?: PblLawyerCardProfile) => {
  if (!profile) return '';

  const cvQualification = trimDisplayText(profile.cv_other_qualifications_en);
  if (cvQualification) return cvQualification;

  return [joinLabels(pblProfessionalQualifications, profile.other_professional_qualifications), profile.other_professional_qualification_text]
    .filter(Boolean)
    .join(', ');
};

const getProfileLanguages = (profile?: PblLawyerCardProfile) => {
  if (!profile) return [];

  const cvLanguages = splitDisplayList(profile.cv_languages_en);
  if (cvLanguages.length > 0) return cvLanguages;

  return [joinLabels(pblLanguages, profile.language_proficiency), profile.language_other_text]
    .filter(Boolean)
    .join(', ')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
};

const getAdmittedYearsFromProfile = (profile?: PblLawyerCardProfile) => {
  const currentYear = getCurrentYear();
  for (const value of [profile?.hk_solicitor_admission_year, profile?.cv_admission_year_en, profile?.cv_admission_year_zh]) {
    const admissionYear = Number(value);
    if (Number.isInteger(admissionYear) && admissionYear >= 1900 && admissionYear <= currentYear) {
      return currentYear - admissionYear;
    }
  }
  return null;
};

const getProfilePrimaryPracticeArea = (profile?: PblLawyerCardProfile) => {
  if (!profile) return '';

  const cvPrimaryPracticeArea = trimDisplayText(profile.cv_primary_practice_areas_en);
  if (cvPrimaryPracticeArea) return cvPrimaryPracticeArea;

  return joinLabels(pblServiceAreas, profile.service_areas);
};

const getProfileProfessionalExperience = (profile?: PblLawyerCardProfile) => {
  if (!profile) return '';

  return trimDisplayText(profile.cv_professional_experience_en) || trimDisplayText(profile.cv_primary_practice_areas_en);
};

export const mapDirectoryEntryToOcLawyer = (entry: PblDirectoryEntry | PblPublicDirectoryEntry): PblOcLawyer => {
  const profile = 'lawyer_profile' in entry ? entry.lawyer_profile || undefined : undefined;
  const serviceArea = profile?.service_areas?.[0] || entry.service_areas?.[0];
  const profileLanguages = getProfileLanguages(profile);
  const entryLanguageLabels = (entry.languages || []).map((language) => optionLabel(pblLanguages, language));
  const primaryPracticeArea = getProfilePrimaryPracticeArea(profile);

  return {
    id: entry.pbl_lawyer_profile_id || profile?.id || entry.id,
    directoryEntryId: entry.id,
    name:
      [profile?.full_name_en || entry.display_name_en, profile?.full_name_zh || entry.display_name_zh]
        .filter(Boolean)
        .join(' / ') || entry.display_name_en,
    qualification: getProfileQualification(profile) || entry.position_in_law_firm || '-',
    admittedYears: getAdmittedYearsFromProfile(profile),
    languages: profileLanguages.length > 0 ? profileLanguages : entryLanguageLabels.length > 0 ? entryLanguageLabels : ['-'],
    primaryPracticeArea: primaryPracticeArea || (serviceArea ? serviceAreaLabelByValue[serviceArea] || serviceArea : '-'),
    completedCases: Number(entry.completed_case_count || 0),
    comments: getProfileProfessionalExperience(profile) || entry.badge_label || '',
    isPreferred: entry.is_preferred === true,
    isPersonalFavorite: entry.is_personal_favorite === true,
    profile,
  };
};

export const isVisiblePblDirectoryEntry = (entry?: PblDirectoryEntry | PblPublicDirectoryEntry | null) =>
  Boolean(
    entry &&
      entry.is_published !== false &&
      entry.is_available !== false &&
      (!('is_hidden' in entry) || entry.is_hidden !== true)
  );

export const mapLawyerProfileToOcLawyer = (profile?: PblLawyerCardProfile | null): PblOcLawyer | null => {
  if (!profile) return null;

  const profileLanguages = getProfileLanguages(profile);
  const primaryPracticeArea = getProfilePrimaryPracticeArea(profile);

  return {
    id: profile.id,
    name: [profile.full_name_en, profile.full_name_zh].filter(Boolean).join(' / ') || profile.full_name_en || '-',
    qualification: getProfileQualification(profile) || profile.position_in_law_firm || '-',
    admittedYears: getAdmittedYearsFromProfile(profile),
    languages: profileLanguages.length ? profileLanguages : ['-'],
    primaryPracticeArea: primaryPracticeArea || '-',
    completedCases: Number(
      (isFullPblLawyerProfile(profile)
        ? profile.completed_case_count
        : profile.directory_entry?.completed_case_count) || 0
    ),
    comments: getProfileProfessionalExperience(profile),
    profile,
  };
};

export const isValidLawyerChoiceReason = (reason: string) => reason.trim().length > 0;

const manualInviteEligibleStatuses = new Set<PblCaseStatus>([
  'initiated',
  'matching_ready',
  'ai_matched',
  'manual_assignment_required',
  'lawyer_declined',
  'lawyer_timeout',
]);

export const canManualInvitePblCase = (status?: PblCaseStatus | string | null) =>
  Boolean(status && manualInviteEligibleStatuses.has(status as PblCaseStatus));

export const buildManualInvitePayload = (lawyer: PblOcLawyer, reason: string): PblManualInvitePayload => {
  const payload = {
    manual_reason: reason.trim(),
  };

  if (typeof lawyer.directoryEntryId === 'number') {
    return {
      ...payload,
      pbl_lawyer_directory_entry_id: lawyer.directoryEntryId,
    };
  }

  return {
    ...payload,
    pbl_lawyer_profile_id: lawyer.id,
  };
};

export const filterPblLawyers = (lawyers: PblOcLawyer[], filters: PblOcLawyerFilters) => {
  const keyword = normalize(filters.keyword);

  return lawyers.filter((lawyer) => {
    const lawyerQualifications = splitDisplayList(lawyer.qualification);
    const lawyerPracticeAreas = splitDisplayList(lawyer.primaryPracticeArea);
    const matchesQualification =
      filters.qualification === 'all' ||
      lawyer.qualification === filters.qualification ||
      lawyerQualifications.includes(filters.qualification);
    const matchesLanguage = filters.language === 'all' || lawyer.languages.includes(filters.language);
    const matchesPracticeArea =
      filters.practiceArea === 'all' ||
      lawyer.primaryPracticeArea === filters.practiceArea ||
      lawyerPracticeAreas.includes(filters.practiceArea);

    if (!matchesQualification || !matchesLanguage || !matchesPracticeArea) return false;
    if (!keyword) return true;

    return [lawyer.name, lawyer.qualification, lawyer.languages.join(' '), lawyer.primaryPracticeArea, lawyer.comments]
      .join(' ')
      .toLowerCase()
      .includes(keyword);
  });
};

const parseLawyerId = (value: string | null) => {
  if (!value) return undefined;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const getInitialMatchingView = (item: PblOcManagedCase, searchParams: URLSearchParams): PblOcMatchingView => {
  const queryLawyerId = parseLawyerId(searchParams.get('lawyerId'));

  if (item.matchingStatus === 'invitation_sent') {
    return { mode: 'invitation_sent', lawyerId: item.selectedLawyerId || item.aiRecommendedLawyerId };
  }

  if (searchParams.get('state') === 'declined' || item.matchingStatus === 'declined') {
    return { mode: 'declined', lawyerId: undefined };
  }

  if (searchParams.get('source') === 'manual' && queryLawyerId) {
    return { mode: 'human_selected', lawyerId: queryLawyerId };
  }

  if (item.matchingStatus === 'human_selected' && item.selectedLawyerId) {
    return { mode: 'human_selected', lawyerId: item.selectedLawyerId };
  }

  return { mode: 'ai_recommended', lawyerId: item.aiRecommendedLawyerId };
};

export const buildLawyerReviewSummary = (
  lawyer: PblOcLawyer,
  language: 'en' | 'zh'
): { form: VolunteerFormState; serviceAreaLabels: string[] } => {
  if (lawyer.profile && isFullPblLawyerProfile(lawyer.profile)) {
    return buildReviewSummaryFromProfile(lawyer.profile);
  }

  const serviceArea = practiceAreaToServiceArea(lawyer.primaryPracticeArea);
  const serviceAreaLabel = serviceAreaLabelByValue[serviceArea];
  const languagesText = lawyer.languages.join(', ');
  const admissionYear = lawyer.admittedYears !== null ? String(getCurrentYear() - lawyer.admittedYears) : '';
  const isChinesePreview = language === 'zh';
  const signedAt = getCurrentFormDate();

  return {
    form: {
      full_name_en: lawyer.name,
      full_name_zh: isChinesePreview ? lawyer.name : '',
      phone: '-',
      phone_otp: '',
      phone_otp_verified: false,
      email: '-',
      email_otp: '',
      email_otp_verified: false,
      password: '',
      password_confirmation: '',
      hk_solicitor_admission_year: admissionYear,
      other_professional_qualifications: [],
      other_professional_qualification_text: '',
      language_proficiency: [],
      language_other_text: '',
      law_firm_name: '-',
      position_in_law_firm: '-',
      position_other_text: '',
      service_areas: [serviceArea],
      cv_publication_consent: true,
      cv_full_name_en: lawyer.name,
      cv_full_name_zh: isChinesePreview ? lawyer.name : '',
      cv_law_firm_en: '-',
      cv_law_firm_zh: isChinesePreview ? '-' : '',
      cv_position_en: lawyer.qualification,
      cv_position_zh: isChinesePreview ? lawyer.qualification : '',
      cv_admission_year_en: admissionYear,
      cv_admission_year_zh: isChinesePreview ? admissionYear : '',
      cv_other_qualifications_en: lawyer.qualification,
      cv_other_qualifications_zh: isChinesePreview ? lawyer.qualification : '',
      cv_languages_en: languagesText,
      cv_languages_zh: isChinesePreview ? languagesText : '',
      cv_primary_practice_areas_en: serviceAreaLabel,
      cv_primary_practice_areas_zh: isChinesePreview ? serviceAreaLabel : '',
      cv_professional_experience_en: lawyer.comments,
      cv_professional_experience_zh: isChinesePreview ? lawyer.comments : '',
      cv_pro_bono_experience_en: `${lawyer.completedCases} completed pro bono case(s).`,
      cv_pro_bono_experience_zh: isChinesePreview ? `${lawyer.completedCases} completed pro bono case(s).` : '',
      cv_signature: lawyer.name,
      cv_signed_at: signedAt,
      firm_authorisation_template_downloaded: true,
      firm_authorisation_file_id: undefined,
      firm_authorisation_confirmed: true,
      firm_authorisation_letter_attached: true,
      law_firm_change_notify_agreed: true,
      service_pledge_agreed: true,
      post_completion_agreed: true,
      pics_agreed: true,
      bankrupt_or_insolvent: false,
      arrangement_with_creditors: false,
      criminal_conviction: false,
      professional_misconduct_or_pending_disciplinary: false,
      suspended_from_professional_practice: false,
      conduct_supporting_document_file_id: undefined,
      declaration_signature: lawyer.name,
      declaration_signed_at: signedAt,
      review_confirmed: true,
    },
    serviceAreaLabels: [serviceAreaLabel],
  };
};
